import React, { useState } from "react";
import Button from "react-bootstrap/Button";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SharedProgramCard } from "@sophy/shared-ui";
import http from "@shared/api/http";
import {
  isActive,
  programInterval,
  getTargetName,
  getEndDateOfProgram,
} from "@features/programs/utils/programHelpers";
import {
  getProgramCategory,
  getCategoryIcon,
} from "@features/programs/utils/programCategory";
import RequestProgramModal from "@features/programs/modals/RequestProgramModal";
import FeedbackDetailsModal from "@features/programs/modals/FeedbackDetailsModal";
import { WeekRow } from "./gamification";
import "./Programs.css";

function ProgramCard(props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackDetails, setFeedbackDetails] = useState(null);
  const programDetailsPath = `/programs/${props.program.id}`;

  const status_program = isActive(
    props.program.created_at,
    props.program.duration
  );
  const category = getProgramCategory(props.program);
  const categoryIcon = getCategoryIcon(category);

  const progress = (() => {
    const len =
      (props.program.sessions.length /
        (props.program.frequency * props.program.duration)) *
      100;
    if (isNaN(len)) return 0;
    return Math.max(0, Math.min(100, len));
  })();

  const sorted_sessions = props.program.sessions
    ?.map((s) => ({ ...s, created_at: new Date(s.created_at) }))
    .sort((a, b) => b.created_at - a.created_at);

  const latestSession = sorted_sessions?.length > 0 ? sorted_sessions[0] : null;

  // ── Last feedback content for the info column ───────────────────────────
  const feedbackContent = latestSession ? (
    <span>
      {latestSession.created_at.toLocaleDateString("de-DE", {
        day: "numeric",
        month: "short",
      })}
      {latestSession.questionnaire_template_id &&
      Number(latestSession.questionnaire_template_id) > 0 ? (
        <button
          type="button"
          className="portal-pc-feedback-btn"
          onClick={async (e) => {
            e.stopPropagation();
            try {
              const url = `/:locale/v2/:token/programs/${props.program.id}/latest_questionnaire_response.json?session_id=${latestSession.id}`;
              const res = await http.get(url);
              setFeedbackDetails(res.data);
              setShowFeedbackModal(true);
            } catch (e) {
              console.error("Error fetching questionnaire details:", e);
            }
          }}
          title={t("questionnaires.feedbackDetails")}
        >
          – {t("questionnaires.feedbackDetails")}
        </button>
      ) : latestSession.program_feedback ? (
        <span style={{ color: "#6b7280" }}> – {latestSession.program_feedback}</span>
      ) : null}
    </span>
  ) : (
    <em style={{ fontStyle: "italic", color: "#9ca3af" }}>
      {t("program.no-feedback", { defaultValue: "Keine Rückmeldung" })}
    </em>
  );

  // ── Meta items ───────────────────────────────────────────────────────────
  const metaItems = [
    {
      label: `${t("program.target", { defaultValue: "Ziel" })}:`,
      value: getTargetName(String(props.program.goal || "")),
    },
    {
      label: `${t("program.frequency", { defaultValue: "Häufigkeit" })}:`,
      value: programInterval(String(props.program.frequency || "")),
    },
    {
      label: `${t("programs.duration", { defaultValue: "Dauer" })}:`,
      value: `${props.program.duration || 0} ${t("program.weeks", { defaultValue: "Wochen" })}`,
    },
  ];

  // ── Action column: badge + details + optional request button ────────────
  const actionsSlot = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        justifyContent: "space-between",
        height: "100%",
        gap: "6px",
      }}
    >
      <div
        className={`sophy-pc-status-badge sophy-pc-status-badge-${status_program ? "active" : "inactive"}`}
      >
        {status_program
          ? t("patients.active", { defaultValue: "Aktiv" })
          : t("patients.inactive", { defaultValue: "Inaktiv" })}
      </div>
      <Link
        to={programDetailsPath}
        className={`sophy-pc-details-link sophy-pc-details-link-${status_program ? "active" : "inactive"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {t("program.details", { defaultValue: "Details" })}
      </Link>
      {!status_program && (
        <Button
          variant="outline-primary"
          size="sm"
          className="portal-pc-request-btn d-none d-md-inline-block"
          onClick={(e) => {
            e.stopPropagation();
            setShowRequestModal(true);
          }}
        >
          {t("program.request_new_program", { defaultValue: "Neues Programm" })}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <SharedProgramCard
        isActive={status_program}
        categoryIcon={categoryIcon}
        categoryName={category}
        programName={props.program.name}
        subtitleLabel={category}
        progress={progress}
        /* This week's sessions against the prescription while the program
           runs; once it has ended, the date it did. */
        mainSlot={
          status_program ? (
            <WeekRow program={props.program} />
          ) : (
            <div className="gm gm-week-row gm-week-row-ended">
              <span>
                {t("gamification.week.ended", {
                  date: new Date(
                    getEndDateOfProgram(props.program.created_at, props.program.duration)
                  ).toLocaleDateString(i18n.language, { day: "numeric", month: "long" }),
                })}
              </span>
            </div>
          )
        }
        metaItems={metaItems}
        infoHeading={t("feedback.last-feedback", { defaultValue: "Letzte Rückmeldung" })}
        infoContent={feedbackContent}
        actionsSlot={actionsSlot}
        onCardClick={() => navigate(programDetailsPath)}
        mobileDetailsLabel={`${t("program.details", { defaultValue: "Details" })} >`}
        onDetailsClick={() => navigate(programDetailsPath)}
      />

      {/* Mobile-only: request button below the card so it doesn't overlap the details link */}
      {!status_program && (
        <div className="d-md-none portal-pc-request-mobile">
          <Button
            variant="outline-primary"
            size="sm"
            className="portal-pc-request-btn w-100"
            onClick={(e) => {
              e.stopPropagation();
              setShowRequestModal(true);
            }}
          >
            {t("program.request_new_program", { defaultValue: "Neues Programm" })}
          </Button>
        </div>
      )}

      <FeedbackDetailsModal
        show={showFeedbackModal}
        onHide={() => setShowFeedbackModal(false)}
        feedbackDetails={feedbackDetails}
      />
      <RequestProgramModal
        show={showRequestModal}
        onHide={() => setShowRequestModal(false)}
        programId={props.program.id}
        onSuccess={() => setShowRequestModal(false)}
      />
    </>
  );
}

export default ProgramCard;
